const React = require('react');
const T = require('prop-types');
const { default: Chip } = require('@mui/material/Chip');
const { default: Button } = require('@mui/material/Button');
const { default: Typography } = require('@mui/material/Typography');
const { default: SearchIcon } = require('@mui/icons-material/Search');
const { default: CancelIcon } = require('@mui/icons-material/Cancel');
const { default: styled } = require('styled-components');
const { transient$Props } = require('utils/styles');

const internals = {};

module.exports = ActiveFilterSearchBar;
module.exports.internals = internals;

function ActiveFilterSearchBar(props) {

    const {
        activeFilters,
        isFilteringResults,
        onClearFilters,
        onOpenFilters,
        placeholder,
        showClearButton
    } = props;

    const visibleFilters = activeFilters.filter((filter) => (
        filter.value !== undefined &&
        filter.value !== null &&
        filter.value !== false
    ));
    const shouldShowClearButton = typeof showClearButton === 'boolean' ? showClearButton : isFilteringResults;

    const {
        ChipContainer,
        ClearButton,
        SearchBar,
        SearchButton,
        SearchText,
        StyledChip,
        StyledSearchIcon,
        Wrapper
    } = internals;

    return (
        <Wrapper>
            <SearchBar $isFilteringResults={isFilteringResults}>
                <SearchButton
                    type='button'
                    $isFilteringResults={isFilteringResults}
                    onClick={onOpenFilters}
                >
                    <StyledSearchIcon />
                    {isFilteringResults ? (
                        <ChipContainer>
                            {visibleFilters.map((filter, index) => (
                                <StyledChip
                                    key={`${filter.label}:${filter.value}:${index}`}
                                    label={filter.labelOnly ? filter.label : `${filter.label}: ${filter.value}`}
                                    size='small'
                                />
                            ))}
                        </ChipContainer>
                    ) : (
                        <SearchText>
                            {placeholder}
                        </SearchText>
                    )}
                </SearchButton>
                {shouldShowClearButton && (
                    <ClearButton
                        color='secondary'
                        onClick={onClearFilters}
                        startIcon={<CancelIcon />}
                    />
                )}
            </SearchBar>
        </Wrapper>
    );
}

ActiveFilterSearchBar.propTypes = {
    activeFilters: T.arrayOf(T.shape({
        label: T.string.isRequired,
        labelOnly: T.bool,
        value: T.any
    })),
    isFilteringResults: T.bool,
    onClearFilters: T.func.isRequired,
    onOpenFilters: T.func.isRequired,
    placeholder: T.string,
    showClearButton: T.bool
};

ActiveFilterSearchBar.defaultProps = {
    activeFilters: [],
    isFilteringResults: false,
    placeholder: 'Search by interests, majors...'
};

internals.Wrapper = styled.div`
    padding: 14px 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

internals.SearchBar = styled('div', transient$Props)`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: ${({ $isFilteringResults }) => $isFilteringResults ? 'start' : 'center'};
    box-sizing: border-box;
    width: 100%;
    min-height: 52px;
    background-color: ${({ theme }) => theme.palette.tertiary.main};
    border-radius: 8px;
    padding: ${({ $isFilteringResults }) => $isFilteringResults ? '12px 16px' : '0 16px'};
    border: none;
    justify-content: ${({ $isFilteringResults }) => $isFilteringResults ? 'space-between' : 'normal'};

    * {
        color: ${({ theme }) => theme.palette.tertiary.contrastText};
    }

    button {
        font: inherit;
    }
`;

internals.SearchButton = styled('button', transient$Props)`
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: ${({ $isFilteringResults }) => $isFilteringResults ? 'start' : 'center'};
    flex: 1 1 auto;
    min-width: 0;
    gap: 12px;
    cursor: pointer;
    border: none;
    background: transparent;
    width: 100%;
    padding: 0;
    text-align: left;

    > * {
        pointer-events: none;
    }
`;

internals.StyledSearchIcon = styled(SearchIcon)`
    flex-shrink: 0;
    color: ${({ theme }) => theme.palette.tertiary.contrastText};
`;

internals.SearchText = styled(Typography)`
    color: ${({ theme }) => theme.palette.tertiary.contrastText};
    opacity: 1;
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
    justify-content: flex-start;
    text-align: left;
    margin: 0;
`;

internals.ChipContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    overflow: visible;
    min-width: 0;
    max-width: 100%;
    width: 100%;
`;

internals.StyledChip = styled(Chip)`
    max-width: 200px;
    box-sizing: border-box;
    color: inherit !important;
    background-color: rgba(0, 0, 0, 0.2) !important;
    border: 1px solid rgba(0, 0, 0, 0.28) !important;
    transition: background-color 160ms ease, border-color 160ms ease;

    .MuiChip-label {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
        font-size: 13px;
        line-height: 20px;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

internals.ClearButton = styled(Button)`
    && {
        align-self: center;
        width: 32px;
        min-width: 32px;
        height: 32px;
        padding: 4px;
        border-radius: 50%;
        margin-left: 8px;
    }

    && .MuiButton-startIcon {
        margin: 0;
    }

    && .MuiButton-startIcon > *:nth-of-type(1) {
        font-size: 20px;
    }

    &&:hover {
        background-color: transparent;
    }
`;
